import { keycloakInstance } from "@king-ict/eupisi-ui/auth";
import { api, axiosBaseQuery } from "@king-ict/eupisi-ui/utils";
import { createApi } from "@reduxjs/toolkit/query/react";

import Request from "../../models/Request/Request";
import {
  AdditionalDocumentationDto,
  InstitutionSelectionDto,
  RequestSummaryDto,
  PersonDetails,
} from "../../models/Request";
import { SaveResult } from "../../models/Shared/SaveResult";

import {
  DEFAULT_REQUEST_FAILED_MESSAGE,
  REQUESTS_URL_PREFIX,
} from "./constants";
import type { SaveRequestArgs } from "./types";
import { isRequestApiError, stripTrailingSlash } from "./utils";

/** Save request via fetch to bypass global Axios (and thus ResponseHandler) for large payloads (e.g. DocumentationData step). */
async function saveRequestWithFetch(
  item: Request,
  endpoint: string
): Promise<SaveResult<Request>> {
  try {
    const base = process.env.REACT_APP_API_URL ?? "";
    const url = `${stripTrailingSlash(base)}/${endpoint}`;

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(keycloakInstance?.token && {
        Authorization: `Bearer ${keycloakInstance.token}`,
      }),
    };

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(item),
      credentials: "include",
    });

    if (!response.ok) {
      let message = DEFAULT_REQUEST_FAILED_MESSAGE;
      try {
        const body = await response.json();
        if (body?.message) {
          message = body.message;
        } else if (body?.error) {
          message =
            typeof body.error === "string"
              ? body.error
              : DEFAULT_REQUEST_FAILED_MESSAGE;
        }
      } catch {
        // ignore parse errors
      }

      throw { message, status: response.status };
    }

    return response.json() as Promise<SaveResult<Request>>;
  } catch (error) {
    console.error("[saveRequestWithFetch] Error:", error);
    throw error;
  }
}

export const requestApi = createApi({
  reducerPath: "requestApi",
  baseQuery: axiosBaseQuery(),
  endpoints: (build) => ({
    getRequestSummary: build.query<RequestSummaryDto, number>({
      query: (requestId) => `${REQUESTS_URL_PREFIX}/summary/${requestId}`,
    }),
    getPersonDetails: build.query<PersonDetails, number>({
      query: (personDetailsId) =>
        `${REQUESTS_URL_PREFIX}/person-details/${personDetailsId}`,
    }),
    getInstitutionSelection: build.query<InstitutionSelectionDto, number>({
      query: (requestId) =>
        `${REQUESTS_URL_PREFIX}/institution-selection/${requestId}`,
    }),
    getAdditionalDocumentation: build.query<AdditionalDocumentationDto, number>(
      {
        query: (requestId) =>
          `${REQUESTS_URL_PREFIX}/additional-documentation/${requestId}`,
      }
    ),
    saveRequest: build.mutation<SaveResult<Request>, SaveRequestArgs>({
      queryFn: async (arg) => {
        const { item, stepType, useFetchForLargePayload } = arg;

        const endpoint = stepType
          ? `${REQUESTS_URL_PREFIX}/save/${stepType}`
          : `${REQUESTS_URL_PREFIX}/save`;

        try {
          const result = useFetchForLargePayload
            ? await saveRequestWithFetch(item, endpoint)
            : ((await api.post(endpoint, item)) as SaveResult<Request>);

          return { data: result };
        } catch (error: unknown) {
          const apiError = isRequestApiError(error) ? error : null;

          const status =
            (error as { status?: number })?.status ?? apiError?.status ?? 0;

          const message =
            (error as { message?: string })?.message ??
            apiError?.message ??
            DEFAULT_REQUEST_FAILED_MESSAGE;

          return { error: { message, status } };
        }
      },
    }),
  }),
});

export const {
  useGetRequestSummaryQuery,
  useLazyGetRequestSummaryQuery,
  useLazyGetPersonDetailsQuery,
  useLazyGetInstitutionSelectionQuery,
  useLazyGetAdditionalDocumentationQuery,
  useSaveRequestMutation,
} = requestApi;
